// The retelling pane's way into the reference lookup from a text selection:
// a "Look up" button floated at the selection. The selection itself is the
// reader's and stays native; this only reads it, snaps it (`lookupSpan`), and
// hands the span to the lookup the chapter's `ContextLinkScope` hosts.

import { use, useEffect, useRef, useState, type RefObject } from "react";
import { BookOpen } from "lucide-react";
import type { DocumentModel } from "@bindery/shared/corpus/document";
import { selectionToOffsets } from "@bindery/reader-dom/dom-offsets";
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
} from "@bindery/ui/components/ui/popover";
import { cn } from "@bindery/ui/lib/utils";
import { lookupSpan, type LookupSpan } from "./lookup-span";
import { ReferenceLookupContext } from "./reference-lookup-context";
import { referenceSelection } from "./reference-selection";

export const RETELLING_SCOPE = '[data-pane="retelling"]';

interface Rect {
  left: number;
  top: number;
  width: number;
  height: number;
}

interface Captured {
  rect: Rect;
  span: LookupSpan;
  returnFocus: HTMLElement | null;
  /** A keyboard selection opens with Enter; focus stays at the reader's caret. */
  via: "pointer" | "keyboard";
}

// A native selection handle drag, select-all or a double click changes the
// selection without the events below, so a settled selectionchange recaptures.
const SELECTION_SETTLE_MS = 200;

const INTERACTIVE =
  'a, button, input, select, textarea, [role="button"], [contenteditable]';

function inOverlay(target: EventTarget | null): boolean {
  return (
    target instanceof Element &&
    target.closest('[data-slot="popover-content"], [role="dialog"]') !== null
  );
}

export function RetellingLookupLayer({
  model,
  rootRef,
  language = "en",
}: {
  model: DocumentModel;
  /** The scroll container the retelling's `[data-start]` runs live in. */
  rootRef: RefObject<HTMLDivElement | null>;
  language?: string;
}) {
  const lookup = use(ReferenceLookupContext);
  const [captured, setCaptured] = useState<Captured | null>(null);
  // Mirrored for the scroll handler: a rect is viewport-fixed, so a scroll
  // dismisses the button, but the listener effect never re-runs on capture.
  const capturedRef = useRef(captured);
  const openRef = useRef<() => void>(() => undefined);
  useEffect(() => {
    capturedRef.current = captured;
  });

  useEffect(() => {
    let rafId: number | null = null;
    let settleId: ReturnType<typeof setTimeout> | null = null;
    let pointerDown = false;
    const capture = (via: Captured["via"]): void => {
      const root = rootRef.current;
      const sel = window.getSelection();
      const offsets = root
        ? selectionToOffsets(root, sel, RETELLING_SCOPE)
        : null;
      const span = offsets
        ? lookupSpan(model, offsets.start, offsets.end)
        : null;
      if (!span || !sel) {
        setCaptured(null);
        return;
      }
      const rect = sel.getRangeAt(0).getBoundingClientRect();
      setCaptured({
        rect: {
          left: rect.left,
          top: rect.top,
          width: rect.width,
          height: rect.height,
        },
        span,
        returnFocus:
          document.activeElement instanceof HTMLElement
            ? document.activeElement
            : null,
        via,
      });
    };
    const schedule = (via: Captured["via"]): void => {
      if (rafId !== null) window.cancelAnimationFrame(rafId);
      rafId = window.requestAnimationFrame(() => capture(via));
    };
    const onPointerDown = (e: MouseEvent | TouchEvent): void => {
      if (!inOverlay(e.target)) pointerDown = true;
    };
    const onMouseUp = (e: MouseEvent): void => {
      pointerDown = false;
      if (e.button !== 0 || inOverlay(e.target)) return;
      schedule("pointer");
    };
    const onTouchEnd = (e: TouchEvent): void => {
      if (e.touches.length === 0) pointerDown = false;
      if (inOverlay(e.target)) return;
      schedule("pointer");
    };
    const onKeyUp = (e: KeyboardEvent): void => {
      if (inOverlay(e.target)) return;
      if (e.key !== "Shift" && !e.key.startsWith("Arrow")) return;
      schedule("keyboard");
    };
    const onKeyDown = (e: KeyboardEvent): void => {
      if (capturedRef.current === null || inOverlay(e.target)) return;
      if (e.key === "Escape") {
        setCaptured(null);
        return;
      }
      if (e.key !== "Enter" || e.repeat) return;
      if (e.target instanceof Element && e.target.closest(INTERACTIVE)) return;
      e.preventDefault();
      openRef.current();
    };
    const onSelectionChange = (): void => {
      if (pointerDown) return;
      const sel = window.getSelection();
      if (!sel || sel.isCollapsed) return;
      if (settleId !== null) clearTimeout(settleId);
      settleId = setTimeout(() => {
        settleId = null;
        schedule(capturedRef.current?.via ?? "pointer");
      }, SELECTION_SETTLE_MS);
    };
    const onScroll = (): void => {
      if (capturedRef.current !== null) setCaptured(null);
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("touchstart", onPointerDown, { passive: true });
    document.addEventListener("mouseup", onMouseUp);
    document.addEventListener("touchend", onTouchEnd, { passive: true });
    document.addEventListener("keyup", onKeyUp);
    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("selectionchange", onSelectionChange);
    window.addEventListener("scroll", onScroll, {
      passive: true,
      capture: true,
    });
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("touchstart", onPointerDown);
      document.removeEventListener("mouseup", onMouseUp);
      document.removeEventListener("touchend", onTouchEnd);
      document.removeEventListener("keyup", onKeyUp);
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("selectionchange", onSelectionChange);
      if (settleId !== null) clearTimeout(settleId);
      window.removeEventListener("scroll", onScroll, { capture: true });
      if (rafId !== null) window.cancelAnimationFrame(rafId);
    };
  }, [model, rootRef]);

  const open = (): void => {
    if (!lookup || !captured) return;
    const selection = referenceSelection(
      model,
      captured.span.start,
      captured.span.end,
      language,
    );
    setCaptured(null);
    window.getSelection()?.removeAllRanges();
    if (selection) lookup.open(selection, captured.returnFocus);
  };
  // Read by the document keydown listener, which never re-registers.
  useEffect(() => {
    openRef.current = open;
  });

  if (!lookup || !captured) return null;

  return (
    <Popover
      open
      onOpenChange={(next) => {
        if (!next) setCaptured(null);
      }}
    >
      <PopoverAnchor asChild>
        <div
          aria-hidden
          style={{
            position: "fixed",
            left: captured.rect.left,
            top: captured.rect.top,
            width: captured.rect.width,
            height: captured.rect.height,
            pointerEvents: "none",
          }}
        />
      </PopoverAnchor>
      <PopoverContent
        side="top"
        align="center"
        sideOffset={8}
        collisionPadding={16}
        aria-label="Selection"
        onOpenAutoFocus={(e) => {
          // Focus stays at the reader's selection: a caret reader would lose
          // their place, and Enter opens a keyboard selection from there.
          e.preventDefault();
        }}
        onCloseAutoFocus={(e) => {
          e.preventDefault();
        }}
        className="w-auto rounded-full p-1"
      >
        {captured.via === "keyboard" && (
          <span className="sr-only" aria-live="polite">
            Press Enter to look up the selection.
          </span>
        )}
        <button
          type="button"
          onClick={open}
          className={cn(
            "text-lookup hover:bg-lookup/10 inline-flex min-h-10 items-center gap-1.5 rounded-full pr-4 pl-3 font-serif text-sm tracking-wide transition-colors",
            "focus-visible:ring-ring/60 focus-visible:ring-2 focus-visible:outline-none",
          )}
        >
          <BookOpen aria-hidden className="size-4" />
          Look up
          {captured.via === "keyboard" && (
            <kbd className="border-rule text-muted-foreground ml-1 rounded border px-1 font-serif text-[11px]">
              Enter
            </kbd>
          )}
        </button>
      </PopoverContent>
    </Popover>
  );
}
